/**
 * Slim, non-modal banner pinned to the top of the note editor viewport when the
 * practitioner has pre-existing STAGED AI suggestions but they aren't painted yet
 * (deferred-paint, S5). On note mount with pre-existing staging, hydration is
 * held back (the decorations would clutter a load view, and not auto-painting
 * mitigates the same-practitioner two-tab double-accept residual); this banner
 * is the opt-in to reveal them IN THIS TAB.
 *
 * - "Review suggestions" → reveal + paint (runs hydration in this tab), banner hides.
 *
 * The banner has NO dismiss affordance: it shows whenever staged suggestions are
 * pending and unrevealed in this tab. The real "dismiss" is to reveal the
 * suggestions and then reject them — a soft hide that left the staging untouched
 * only created a way to lose track of pending work (Claude task #5). To return
 * from a revealed view to the banner without deciding, use the status bar's
 * "Hide" control (task #4), which un-paints and re-shows this banner.
 *
 * The staged suggestions may be from a previous session OR another open tab, so
 * the copy says "staged on this note" rather than "from a previous session".
 *
 * Mirrors `AIProposalStatusBar`'s slim sticky-bar pattern (same @seen/ui
 * Button, Tabler icons, and purple tint) so the two read as one family.
 */
import { Button, cn } from '@seen/ui'
import { IconLoader2, IconSparkles } from '@tabler/icons-react'
import type { ReactNode } from 'react'

export function StagedSuggestionsBanner({
  count,
  onReview,
  revealing = false,
  stickyTopClassName = 'top-4',
}: {
  /**
   * Number of decisions still pending (`pendingDecisionCount`), not number of
   * staged records: a questionnaire record holds one decision per answer atom.
   */
  count: number
  /** Reveal + paint the staged suggestions in this tab; the host hides the banner. */
  onReview: () => void
  /**
   * A reveal has been requested but the suggestions aren't painted yet. The host
   * keeps the banner mounted through this window (it normally unmounts the
   * instant `revealed` flips) because the paint waits on the collaborative doc's
   * connect + initial sync, which can run for seconds — long enough that
   * unmounting the banner and painting nothing reads as a broken button.
   */
  revealing?: boolean
  /**
   * Sticky offset from the top of the bar's scroll container. Defaults to
   * `top-4`; pass a header-clearing offset (e.g. `top-16`) on surfaces that
   * render the editor in a shared ScrollArea alongside a sticky nav header —
   * matches `AIProposalStatusBar`'s `stickyTopClassName`.
   */
  stickyTopClassName?: string
}): ReactNode {
  if (count <= 0) {
    return null
  }
  return (
    <output
      aria-live="polite"
      aria-busy={revealing}
      className={cn(
        'sticky z-20 flex items-center justify-between gap-3 rounded-md border border-purple-200 bg-purple-50 px-3 py-2 text-sm shadow-sm dark:border-purple-900 dark:bg-purple-950',
        stickyTopClassName
      )}
    >
      <div className="flex items-center gap-2 text-purple-900 dark:text-purple-200">
        <IconSparkles className="size-4 shrink-0" />
        <span>
          {revealing
            ? `Loading ${count} AI suggestion${count === 1 ? '' : 's'}…`
            : `${count} AI suggestion${count === 1 ? '' : 's'} staged on this note.`}
        </span>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <Button type="button" size="sm" onClick={onReview} disabled={revealing}>
          {revealing && <IconLoader2 className="size-3.5 animate-spin" />}
          {revealing ? 'Loading suggestions' : 'Review suggestions'}
        </Button>
      </div>
    </output>
  )
}
